import React, { createContext, useContext, useReducer, useCallback, useEffect, useState } from 'react';
import { api } from '../config/api';
import { canUseLocalStorage, hasCookieConsent } from '../components/CookieConsent';

const BookingContext = createContext();
export { BookingContext };

// Load user details from localStorage (only if consent given)
const loadUserDetails = () => {
  // console.log('Loading user details from localStorage...');
  // console.log('Can use localStorage:', canUseLocalStorage());
  // console.log('Has necessary consent:', hasCookieConsent('necessary'));
  // console.log('Has preferences consent:', hasCookieConsent('preferences'));
  // Check if we can use localStorage (necessary cookies are always allowed)
  if (!canUseLocalStorage()) {
    // console.log('Cannot load user details - cookie consent not given for preferences');
    return null;
  }
  
  try {
    const saved = localStorage.getItem('mycinemas_userDetails');
    // console.log('Raw saved data:', saved);
    if (saved) {
      const userDetails = JSON.parse(saved);
      // console.log('Parsed user details:', userDetails);
      // Check if user details are still valid (not expired)
      // Also check if it has at least one identifier (userId, name, or verified)
      if (userDetails && (userDetails.userId || userDetails.id || userDetails.name || userDetails.verified)) {
        // console.log('Loaded user details from localStorage:', userDetails);
        return userDetails;
      } else {
        console.warn('Invalid user details in localStorage, clearing...');
        localStorage.removeItem('mycinemas_userDetails');
      }
    }
  } catch (error) {
    console.error('Error loading user details from localStorage:', error);
  }
  return null;
};

// Save user details to localStorage (only if consent given)
const saveUserDetails = (userDetails) => {
  // Check if we can use localStorage (necessary cookies are always allowed)
  if (!canUseLocalStorage()) {
    // console.log('Cannot save user details - cookie consent not given for preferences');
    return;
  }
  
  try {
    if (userDetails) {
      localStorage.setItem('mycinemas_userDetails', JSON.stringify(userDetails));
    } else {
      localStorage.removeItem('mycinemas_userDetails');
    }
  } catch (error) {
    console.error('Error saving user details to localStorage:', error);
  }
};

const initialState = {
  movie: null,
  showtime: null,
  theater: null,
  selectedSeats: [],
  selectedSnacks: [],
  userDetails: loadUserDetails(), // Load from localStorage on init
  selectedDate: null,
  vistaTransactionId: null, // Vista transaction ID from booking initialization
  vistaSessionId: null, // Vista session ID associated with the transaction ID (for validation)
  quantity: null, // Number of tickets to book (1-10)
  intentionalLogout: false, // Track if user intentionally logged out
};

const bookingReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MOVIE':
      return { ...state, movie: action.payload };
    case 'SET_SHOWTIME':
      // Only update if showtime actually changed
      const currentShowtimeId = state.showtime?.id || state.showtime?.sessionId || state.showtime?.vistaShowId;
      const newShowtimeId = action.payload?.id || action.payload?.sessionId || action.payload?.vistaShowId;
      if (currentShowtimeId === newShowtimeId && state.showtime && action.payload) {
        // Showtime hasn't changed, don't update
        return state;
      }
      return { ...state, showtime: action.payload };
    case 'SET_THEATER':
      return { ...state, theater: action.payload };
    case 'SET_SELECTED_SEATS':
      return { ...state, selectedSeats: action.payload };
    case 'SET_SELECTED_SNACKS':
      return { ...state, selectedSnacks: action.payload };

      case 'SET_USER_DETAILS':
        if (action.payload && (action.payload.userId || action.payload.id)) {
          // console.log('Saving user details to localStorage:', action.payload);
          saveUserDetails(action.payload);
          return { ...state, userDetails: action.payload, intentionalLogout: false };
        } else {
          console.warn('Ignoring invalid userDetails payload:', action.payload);
          return state; // 👈 NEVER auto-logout here
        }
      

    case 'SET_SELECTED_DATE':
      return { ...state, selectedDate: action.payload };
    case 'SET_VISTA_TRANSACTION_ID':
      // Store transaction ID along with session ID to ensure consistency
      const { transactionId, sessionId } = action.payload || {};
      return { 
        ...state, 
        vistaTransactionId: transactionId || action.payload, // Support both object and string
        vistaSessionId: sessionId || null,
      };
    case 'SET_QUANTITY':
      return { ...state, quantity: action.payload };
    case 'CLEAR_BOOKING':
      // Clear booking data but preserve user login
      // Only clear booking-related fields, keep userDetails
      return {
        movie: null,
        showtime: null,
        theater: null,
        selectedSeats: [],
        selectedSnacks: [],
        selectedDate: null,
        vistaTransactionId: null, // Clear Vista transaction ID when clearing booking
        vistaSessionId: null, // Clear Vista session ID when clearing booking
        quantity: null, // Clear quantity when clearing booking
        userDetails: state.userDetails, // Preserve user login
      };
    case 'CLEAR_USER':
      // Only clear user, keep booking data
      saveUserDetails(null);
      // Also clear legacy user data
      try {
        localStorage.removeItem('user');
      } catch (error) {
        console.error('Error clearing legacy user data:', error);
      }
      return { ...state, userDetails: null, intentionalLogout: true };
    case 'UPDATE_BOOKING':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

export const BookingProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);

  // Use a function to initialize state to ensure localStorage is checked on every mount
  const [state, dispatch] = useReducer(bookingReducer, () => {
    // Initialize with userDetails loaded from localStorage
    const savedUserDetails = loadUserDetails();
    return {
      ...initialState,
      userDetails: savedUserDetails || null,
    };
  });

  // Check if user intentionally logged out by checking if localStorage was cleared
  const checkIntentionalLogout = () => {
    const hasUserData = loadUserDetails();
    const hasLegacyData = localStorage.getItem('user');
    return !hasUserData && !hasLegacyData;
  };

  // Check and validate user session on mount (similar to admin panel)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const savedUserDetails = loadUserDetails();
        
        // If no saved user details and user didn't intentionally logout, check for legacy data
        if (!savedUserDetails && !checkIntentionalLogout()) {
          // Check if there's a user in localStorage with a different key
          try {
            const legacyUser = localStorage.getItem('user');
            if (legacyUser) {
              // console.log('Found legacy user data:', legacyUser);
              const parsedUser = JSON.parse(legacyUser);
              if (parsedUser && parsedUser.verified) {
                // console.log('Migrating legacy user to new format');
                saveUserDetails(parsedUser);
                dispatch({ type: 'SET_USER_DETAILS', payload: parsedUser });
                return;
              }
            }
          } catch (error) {
            console.error('Error checking legacy user data:', error);
          }
        }
        
        // If user intentionally logged out, don't restore session
        if (state.intentionalLogout || checkIntentionalLogout()) {
          // console.log('User intentionally logged out, not restoring session');
          return;
        }
        
        if (savedUserDetails && (savedUserDetails.userId || savedUserDetails.id)) {
          // console.log('Found saved user details:', savedUserDetails);
          const userId = savedUserDetails.userId || savedUserDetails.id;
          
          // Validate user by fetching from server (like admin panel does)
          try {
            const userData = await api.getUserById(userId);
            
            // ALWAYS preserve the verified status from saved user details
            // Never let server override the authentication status
            const validatedUserDetails = {
              userId: savedUserDetails.userId || savedUserDetails.id,
              id: savedUserDetails.userId || savedUserDetails.id,
              name: userData.name ?? savedUserDetails.name,
              email: userData.email ?? savedUserDetails.email,
              phone: userData.mobile || userData.phone || savedUserDetails.phone || '',
              mobile: userData.mobile || userData.phone || savedUserDetails.mobile || '',
              verified: savedUserDetails.verified, // NEVER override existing verified status
            };
            
            // ('Preserving verified status:', savedUserDetails.verified);
            // console.log('User session validated, updating user details:', validatedUserDetails);
            dispatch({ type: 'SET_USER_DETAILS', payload: validatedUserDetails });
          } catch (error) {
            console.warn('User validation failed, keeping existing session:', error);
            // DO NOT clear session here
            // Just keep old session instead of logging out
          }
          
         } else {
            // console.log('No saved user details found or invalid format:', savedUserDetails);
            // No saved user details found
            // DO NOT clear current session automatically
            console.warn('No saved session found, preserving current state');
          }
          
      } catch (error) {
        console.error('Error checking auth, preserving session:', error);
        // Do not clear user session on network/backend error
      }
      finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []); // Only run once on mount

  // Listen for cookie consent changes
  useEffect(() => {
    const handleConsentUpdate = (event) => {
      const consent = event.detail;
      // If preferences consent was just enabled, try to load saved user details
      if (consent.preferences || consent.necessary) {
        const savedUserDetails = loadUserDetails();
        if (savedUserDetails) {
          dispatch({ type: 'SET_USER_DETAILS', payload: savedUserDetails });
        }
      } else {
        // If preferences consent was revoked, clear saved user details but keep current in state
        // console.log('Preferences consent revoked - user details will not be saved');
      }
    };

    window.addEventListener('cookieConsentUpdated', handleConsentUpdate);
    return () => {
      window.removeEventListener('cookieConsentUpdated', handleConsentUpdate);
    };
  }, []);

  const setMovie = useCallback((movie) => dispatch({ type: 'SET_MOVIE', payload: movie }), []);
  const setShowtime = useCallback((showtime) => dispatch({ type: 'SET_SHOWTIME', payload: showtime }), []);
  const setTheater = useCallback((theater) => dispatch({ type: 'SET_THEATER', payload: theater }), []);
  const setSelectedSeats = useCallback((seats) => dispatch({ type: 'SET_SELECTED_SEATS', payload: seats }), []);
  const setSelectedSnacks = useCallback((snacks) => dispatch({ type: 'SET_SELECTED_SNACKS', payload: snacks }), []);
  const setUserDetails = useCallback((details) => dispatch({ type: 'SET_USER_DETAILS', payload: details }), []);
  const setSelectedDate = useCallback((date) => dispatch({ type: 'SET_SELECTED_DATE', payload: date }), []);
  const setVistaTransactionId = useCallback((transactionId) => dispatch({ type: 'SET_VISTA_TRANSACTION_ID', payload: transactionId }), []);
  const setQuantity = useCallback((quantity) => dispatch({ type: 'SET_QUANTITY', payload: quantity }), []);
  const clearBooking = useCallback(() => dispatch({ type: 'CLEAR_BOOKING' }), []);
  const clearUser = useCallback(() => dispatch({ type: 'CLEAR_USER' }), []);
  const updateBooking = useCallback((data) => dispatch({ type: 'UPDATE_BOOKING', payload: data }), []);

  const value = {
    ...state,
    loading, // Add loading state
    setMovie,
    setShowtime,
    setTheater,
    setSelectedSeats,
    setSelectedSnacks,
    setUserDetails,
    setSelectedDate,
    setVistaTransactionId,
    setQuantity,
    clearBooking,
    clearUser,
    updateBooking
  };

  // Show loading state while checking auth (similar to admin panel)
  if (loading) {
    return (
      <BookingContext.Provider value={value}>
        {children}
      </BookingContext.Provider>
    );
  }

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};
